import { generateContactWithSubItems } from "@/lib/data/factories/contact-factory";
import {
  useCreateContact,
  useCreateContactAddress,
  useCreateContactEmail,
  useCreateContactFax,
  useCreateContactNote,
  useCreateContactPhone,
  useCreateContactWebsite,
} from "@/lib/data/react-query";
import { isPossibleDuplicateResponse } from "@/lib/data/requests";
import { type APIResponse, type Contact } from "@/lib/types";
import { useCallback } from "react";

interface UseCreateMockContactOptions {
  /** Callback function called after the main contact is created but before sub-items */
  onContactCreated?: (contact: APIResponse<Contact>) => void;
  /** Callback function called after all sub-items are created */
  onCompleted?: (contact: APIResponse<Contact>) => void;
  /** Whether to create sub-items (addresses, emails, etc.) or just the main contact */
  includeSubItems?: boolean;
}

export const useCreateMockContact = (
  options: UseCreateMockContactOptions = {},
) => {
  const { onContactCreated, onCompleted, includeSubItems = true } = options;

  const createContact = useCreateContact();
  const createContactAddress = useCreateContactAddress();
  const createContactEmail = useCreateContactEmail();
  const createContactPhone = useCreateContactPhone();
  const createContactFax = useCreateContactFax();
  const createContactNote = useCreateContactNote();
  const createContactWebsite = useCreateContactWebsite();

  const createMockContact = useCallback(async () => {
    const { contact, addresses, emails, faxes, notes, phones, websites } =
      generateContactWithSubItems();

    try {
      // Create the main contact first
      const contactRes = await createContact.mutateAsync(contact);
      if (isPossibleDuplicateResponse(contactRes)) {
        throw new Error("A mock contact matched an existing contact.");
      }

      // Call the callback after contact creation
      onContactCreated?.(contactRes);

      if (includeSubItems) {
        // Create all sub-items in parallel
        const subItemPromises = [
          // Create addresses
          ...addresses.map((address) =>
            createContactAddress.mutateAsync({
              ...address,
              contactId: contactRes.data.id,
            }),
          ),
          // Create emails
          ...emails.map((email) =>
            createContactEmail.mutateAsync({
              ...email,
              contactId: contactRes.data.id,
            }),
          ),
          // Create phones
          ...phones.map((phone) =>
            createContactPhone.mutateAsync({
              ...phone,
              contactId: contactRes.data.id,
            }),
          ),
          // Create faxes
          ...faxes.map((fax) =>
            createContactFax.mutateAsync({
              ...fax,
              contactId: contactRes.data.id,
            }),
          ),
          // Create notes
          ...notes.map((note) =>
            createContactNote.mutateAsync({
              contactId: contactRes.data.id,
              draft: note.draft,
              message: note.message,
            }),
          ),
          // Create websites
          ...websites.map((website) =>
            createContactWebsite.mutateAsync({
              ...website,
              contactId: contactRes.data.id,
            }),
          ),
        ];

        // Wait for all sub-items to be created
        await Promise.all(subItemPromises);
      }

      // Call completion callback
      onCompleted?.(contactRes);

      return contactRes;
    } catch (error) {
      console.error("Error creating mock contact:", error);
      throw error;
    }
  }, [
    createContact,
    createContactAddress,
    createContactEmail,
    createContactPhone,
    createContactFax,
    createContactNote,
    createContactWebsite,
    onContactCreated,
    onCompleted,
    includeSubItems,
  ]);

  return {
    createMockContact,
    isLoading: createContact.isPending,
    error: createContact.error,
    // Expose individual mutation states if needed
    mutations: {
      contact: createContact,
      address: createContactAddress,
      email: createContactEmail,
      phone: createContactPhone,
      fax: createContactFax,
      note: createContactNote,
      website: createContactWebsite,
    },
  };
};
